import type { Notification } from '@parlay-picker/shared/notification';
import type { NotificationDisplayData } from '@parlay-picker/shared/notificationDisplay';
import { getTeamAbbr, getTeamLogo } from '@parlay-picker/shared/teams';
import { usePlayerHeadshotUrl } from '@views/picks/utils';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { COLOR_SCHEME_CLASSES } from './colorScheme';
import { FallbackBody } from './FallbackBody';

const SHARP_SIGNAL_LABELS: Record<string, string> = {
  rlm: 'Reverse Line Movement',
  positive_ev: '+EV',
  consensus: 'Public Consensus',
  sharp_line: 'Sharp Line',
};

/** Monitoring body: player headshot + type-specific detail (line movement / injury / sharp signal) */
export function MonitoringBody({
  displayData,
  notification,
}: {
  displayData: NotificationDisplayData;
  notification: Notification;
}): ReactElement {
  const { monitoring } = displayData;
  const [imgError, setImgError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const headshotUrl = usePlayerHeadshotUrl(monitoring?.player, monitoring?.leagueKey ?? null);
  if (!monitoring) return <FallbackBody notification={notification} />;

  const colors = COLOR_SCHEME_CLASSES[displayData.colorScheme];
  const { leagueKey: monitoringLeagueKey } = monitoring;
  const teamAbbr = monitoring.team ? getTeamAbbr(monitoring.team, monitoringLeagueKey) : '';
  const teamLogoUrl = getTeamLogo(monitoring.team, monitoringLeagueKey);

  return (
    <div className="flex items-start gap-2 mt-1.5">
      {/* Player headshot with team badge */}
      <div className="relative shrink-0 mt-0.5">
        {headshotUrl && !imgError ? (
          <img
            src={headshotUrl}
            alt={monitoring.player}
            className="w-11 h-11 rounded-full object-cover bg-theme-surface-hover ring-2 ring-theme-border/50"
            onError={() => setImgError(true)}
          />
        ) : (
          <div
            className={`w-11 h-11 rounded-full flex items-center justify-center text-xs font-bold ${colors.iconBg} ${colors.iconText}`}
          >
            {monitoring.player
              .split(' ')
              .map((w) => w[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </div>
        )}
        {teamLogoUrl && !logoError && (
          <img
            src={teamLogoUrl}
            alt={teamAbbr}
            className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-theme-surface border border-theme-border"
            onError={() => setLogoError(true)}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-theme-text">{monitoring.player}</p>
        {monitoring.statLabel && (
          <p className="text-xs text-theme-text-secondary mt-0.5">
            {[monitoring.overUnder, monitoring.statLabel].filter(Boolean).join(' ')}
          </p>
        )}

        {monitoring.monitoringType === 'line_movement' &&
          monitoring.openingLine != null &&
          monitoring.currentLine != null && (
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-xs text-theme-text-secondary">
                {monitoring.openingLine} → {monitoring.currentLine}
              </span>
              {monitoring.lineMovement != null && (
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${colors.badgeBg} ${colors.badgeText}`}
                >
                  {monitoring.lineMovement > 0 ? '+' : ''}
                  {monitoring.lineMovement.toFixed(1)}
                </span>
              )}
            </div>
          )}

        {monitoring.monitoringType === 'injury' && (
          <div className="flex items-center gap-1.5 mt-1">
            {monitoring.previousInjuryStatus && (
              <span className="text-xs text-theme-text-tertiary">
                {monitoring.previousInjuryStatus} →
              </span>
            )}
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${colors.badgeBg} ${colors.badgeText}`}
            >
              {monitoring.injuryStatus}
            </span>
          </div>
        )}

        {monitoring.monitoringType === 'sharp_signal' && monitoring.sharpSignalType && (
          <div className="mt-1 flex flex-col gap-1">
            {monitoring.openingLine != null && monitoring.currentLine != null && (
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-theme-text-secondary">
                  {monitoring.openingLine} → {monitoring.currentLine}
                </span>
                {monitoring.lineMovement != null && (
                  <span className="font-mono text-[10px] text-theme-text-tertiary">
                    ({monitoring.lineMovement > 0 ? '+' : ''}
                    {monitoring.lineMovement.toFixed(1)})
                  </span>
                )}
              </div>
            )}
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded self-start ${colors.badgeBg} ${colors.badgeText}`}
            >
              {monitoring.sharpSignalType === 'positive_ev' && monitoring.evPercent != null
                ? `+EV (${monitoring.evPercent.toFixed(1)}%)`
                : (SHARP_SIGNAL_LABELS[monitoring.sharpSignalType] ?? monitoring.sharpSignalType)}
            </span>
          </div>
        )}

        {notification.data.details && (
          <p className="text-xs text-theme-text-tertiary mt-1">{notification.data.details}</p>
        )}
      </div>
    </div>
  );
}
